import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { I18nProvider } from '@/lib/i18n';
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import DemoTour from '@/components/DemoTour';
import DemoBanner from '@/components/DemoBanner';

// Lazy-loaded pages
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const ProjectList = lazy(() => import('@/pages/ProjectList'));
const Editor = lazy(() => import('@/pages/Editor'));
const Diagnostics = lazy(() => import('@/pages/Diagnostics'));
const BillOfQuantities = lazy(() => import('@/pages/BillOfQuantitiesPage'));
const TechnicalReport = lazy(() => import('@/pages/TechnicalReportPage'));
const DocNumbering = lazy(() => import('@/pages/DocNumberingPage'));
const FloorPlanPage = lazy(() => import('@/pages/FloorPlanPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const SetupPage = lazy(() => import('@/pages/SetupPage'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const VersionPage = lazy(() => import('@/pages/VersionPage'));
const SymbolGalleryPage = lazy(() => import('@/pages/SymbolGalleryPage'));
const DemoRegisterPage = lazy(() => import('@/pages/DemoRegisterPage'));
const DemoActivatePage = lazy(() => import('@/pages/DemoActivatePage'));
const DemoLoginPage = lazy(() => import('@/pages/DemoLoginPage'));
const BuyLicensePage = lazy(() => import('@/pages/BuyLicensePage'));
import '@/App.css';

const LoadFallback = () => (
  <div className="h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      <span className="text-sm text-muted-foreground">Nacitam...</span>
    </div>
  </div>
);

// Dynamic login path based on current date: /admin-DD-MM
function getDynamicLoginPath() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `/admin-${dd}-${mm}`;
}

// Global error handler
const API = process.env.REACT_APP_BACKEND_URL;
window.addEventListener('error', (event) => {
  if (event.message?.includes?.('ResizeObserver')) return;
  fetch(`${API}/api/diagnostics/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: event.message, stack: event.error?.stack || '',
      url: event.filename, component: 'window.onerror',
    }),
  }).catch(() => {});
});
window.addEventListener('unhandledrejection', (event) => {
  fetch(`${API}/api/diagnostics/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: event.reason?.message || String(event.reason),
      stack: event.reason?.stack || '', url: window.location.href,
      component: 'unhandledrejection',
    }),
  }).catch(() => {});
});

function P({ children }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function PA({ children }) {
  return <ProtectedRoute requireAdmin>{children}</ProtectedRoute>;
}

function App() {
  const loginPath = getDynamicLoginPath();

  return (
    <I18nProvider>
      <div className="dark min-h-screen bg-background text-foreground">
        <BrowserRouter>
          <AuthProvider>
            <DemoBanner />
            <Routes>
              {/* Public: Landing page */}
              <Route path="/" element={<Suspense fallback={<LoadFallback />}><LandingPage /></Suspense>} />

              {/* Public: Demo registration */}
              <Route path="/demo" element={<Suspense fallback={<LoadFallback />}><DemoRegisterPage /></Suspense>} />
              <Route path="/demo/activate" element={<Suspense fallback={<LoadFallback />}><DemoActivatePage /></Suspense>} />
              <Route path="/demo/login" element={<Suspense fallback={<LoadFallback />}><DemoLoginPage /></Suspense>} />
              <Route path="/koupit" element={<Suspense fallback={<LoadFallback />}><BuyLicensePage /></Suspense>} />

              {/* Public: Version page (basic info public, full info shown if logged in) */}
              <Route path="/version" element={<Suspense fallback={<LoadFallback />}><VersionPage /></Suspense>} />
              {/* Symbol gallery — needs auth (uses /api/element-types) */}
              <Route path="/symbols" element={<P><Suspense fallback={<LoadFallback />}><SymbolGalleryPage /></Suspense></P>} />

              {/* Initial setup (first install) */}
              <Route path="/setup" element={<Suspense fallback={<LoadFallback />}><SetupPage /></Suspense>} />

              {/* Hidden login: /admin-DD-MM */}
              <Route path={loginPath} element={<Suspense fallback={<LoadFallback />}><LoginPage /></Suspense>} />
              {/* Fallback login for direct /login links */}
              <Route path="/login" element={<Suspense fallback={<LoadFallback />}><LoginPage /></Suspense>} />

              {/* Protected routes */}
              <Route path="/projekty" element={<P><Suspense fallback={<LoadFallback />}><ProjectList /></Suspense></P>} />
              <Route path="/editor/:projectId" element={<P><Suspense fallback={<LoadFallback />}><Editor /></Suspense></P>} />
              <Route path="/editor/:projectId/vykaz-vymer" element={<P><Suspense fallback={<LoadFallback />}><BillOfQuantities /></Suspense></P>} />
              <Route path="/editor/:projectId/technicka-zprava" element={<P><Suspense fallback={<LoadFallback />}><TechnicalReport /></Suspense></P>} />
              <Route path="/editor/:projectId/cislovani" element={<P><Suspense fallback={<LoadFallback />}><DocNumbering /></Suspense></P>} />
              <Route path="/new-floorplan" element={<P><Suspense fallback={<LoadFallback />}><FloorPlanPage /></Suspense></P>} />
              <Route path="/diagnostika" element={<P><Suspense fallback={<LoadFallback />}><Diagnostics /></Suspense></P>} />

              {/* Admin routes */}
              <Route path="/users" element={<PA><Suspense fallback={<LoadFallback />}><UserManagement /></Suspense></PA>} />

              {/* Catch-all: redirect unknown routes (incl. yesterday's /admin-DD-MM) to landing */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <DemoTour />
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" offset={{ top: 55 }} />
      </div>
    </I18nProvider>
  );
}

export default App;
